// Чистая функция-валидатор для пути attach (file-picker / DnD / paste).
// Возвращает что принять, что отбросить и почему — caller сам показывает
// toast'ы (зависит от i18n контекста).
//
// Шаги 4-5: принимает per-kind лимиты режима + AttachmentPolicy с бэка
// (mimeTypes/maxSizeMb для каждого kind). Поддерживает все три kind'а
// одновременно (для OmniReference): на каждый kind свой счётчик слотов
// и свои mime/size ограничения.

import type { AttachmentKind } from "~/entities/chat";

export type AttachReject =
    /** Текущий режим вообще не принимает файлы (все per-kind лимиты = 0/null). */
    | "modeNoFiles"
    /** Все файлы non-supported по mime/kind (например тащим pdf на image-only режим). */
    | "unsupportedKind"
    /** Все файлы превышают max-size своего kind по policy. */
    | "tooLarge"
    /** Все слоты по принимаемым kind'ам уже заняты до этого drop'а. */
    | "maxReached";

export interface ValidateAttachResult {
    accepted: File[];
    /** Файлы, которые отброшены по любой причине (mime, kind, размер, лимит). */
    droppedTotal: number;
    /** Если ВСЕ входящие файлы отброшены — почему именно. Иначе null
     * (часть приняли, для остатка caller покажет «частично добавлено»). */
    rejectReason: AttachReject | null;
    /** Если rejectReason == "tooLarge" — лимит размера в МБ из policy
     * (для toast'а с конкретным числом). */
    rejectMaxSizeMb?: number;
}

export interface KindLimits {
    /** Максимум по kind. null = kind не принимается режимом. */
    image: number | null;
    video: number | null;
    audio: number | null;
}

export interface KindPolicy {
    mimeTypes: string[];
    maxSizeMb: number;
}

export interface ValidateAttachInput {
    files: File[];
    /** Per-kind лимиты из текущего режима (`mode.maxInputImages` etc). */
    limits: KindLimits;
    /** Уже в pendingByChat для этого чата по каждому kind. */
    currentCounts: { image: number; video: number; audio: number };
    /** Policy с бэка — `mimeTypes[]` / `maxSizeMb` для каждого kind.
     * Если null (ещё не загрузилась) — пропускаем mime/size валидацию,
     * сервер всё равно проверит. */
    policy?: { image: KindPolicy; video: KindPolicy; audio: KindPolicy } | null;
}

const KIND_BY_MIME_PREFIX: ReadonlyArray<readonly [string, AttachmentKind]> = [
    ["image/", "image"],
    ["video/", "video"],
    ["audio/", "audio"],
];

function detectKind(file: File): AttachmentKind | null {
    for (const [prefix, kind] of KIND_BY_MIME_PREFIX) {
        if (file.type.startsWith(prefix)) return kind;
    }
    return null;
}

interface KindedFile {
    file: File;
    kind: AttachmentKind;
}

interface PolicyFilterResult {
    byPolicy: KindedFile[];
    droppedByMime: number;
    droppedBySize: number;
    firstTooLargeMaxSizeMb: number | undefined;
}

/** Kind'ы, которые режим реально принимает (per-kind лимит > 0). */
function resolveAcceptedKinds(limits: KindLimits): AttachmentKind[] {
    return (["image", "video", "audio"] as const).filter((k) => (limits[k] ?? 0) > 0);
}

/** Шаг 1 — фильтр по kind (mime-prefix), отбрасывая не-принимаемые режимом. */
function filterByAcceptedKind(files: File[], acceptedKinds: AttachmentKind[]): KindedFile[] {
    return files
        .map((f) => ({ file: f, kind: detectKind(f) }))
        .filter((x): x is KindedFile => x.kind != null && acceptedKinds.includes(x.kind));
}

/** Шаг 2 — фильтр по policy (точный mime + maxSizeMb), считая причины отбрасывания. */
function filterByPolicy(byKind: KindedFile[], policy: ValidateAttachInput["policy"]): PolicyFilterResult {
    let firstTooLargeMaxSizeMb: number | undefined;
    let droppedByMime = 0;
    let droppedBySize = 0;
    const byPolicy: KindedFile[] = [];
    for (const item of byKind) {
        const p = policy?.[item.kind];
        if (p) {
            if (p.mimeTypes.length > 0 && !p.mimeTypes.includes(item.file.type)) {
                droppedByMime += 1;
                continue;
            }
            if (p.maxSizeMb > 0 && item.file.size > p.maxSizeMb * 1024 * 1024) {
                droppedBySize += 1;
                firstTooLargeMaxSizeMb ??= p.maxSizeMb;
                continue;
            }
        }
        byPolicy.push(item);
    }
    return { byPolicy, droppedByMime, droppedBySize, firstTooLargeMaxSizeMb };
}

/** Шаг 3 — per-kind слоты: принять по одному, пока есть свободные места kind'а. */
function sliceByRemainingSlots(
    byPolicy: KindedFile[],
    limits: KindLimits,
    currentCounts: ValidateAttachInput["currentCounts"],
): File[] {
    const remaining: Record<AttachmentKind, number> = {
        image: Math.max(0, (limits.image ?? 0) - currentCounts.image),
        video: Math.max(0, (limits.video ?? 0) - currentCounts.video),
        audio: Math.max(0, (limits.audio ?? 0) - currentCounts.audio),
    };
    const accepted: File[] = [];
    for (const item of byPolicy) {
        if (remaining[item.kind] > 0) {
            accepted.push(item.file);
            remaining[item.kind] -= 1;
        }
    }
    return accepted;
}

export function validateAndSliceFiles({
    files,
    limits,
    currentCounts,
    policy,
}: ValidateAttachInput): ValidateAttachResult {
    const acceptedKinds = resolveAcceptedKinds(limits);
    if (acceptedKinds.length === 0) {
        return { accepted: [], droppedTotal: files.length, rejectReason: "modeNoFiles" };
    }

    const byKind = filterByAcceptedKind(files, acceptedKinds);
    if (byKind.length === 0) {
        return { accepted: [], droppedTotal: files.length, rejectReason: "unsupportedKind" };
    }

    const { byPolicy, droppedByMime, droppedBySize, firstTooLargeMaxSizeMb } = filterByPolicy(byKind, policy);
    if (byPolicy.length === 0) {
        // Все срезались. Если хоть один — по size, показываем tooLarge с
        // конкретным MB (более информативно). Иначе unsupportedKind.
        const reason: AttachReject =
            droppedBySize > 0 && droppedByMime === 0 ? "tooLarge" : "unsupportedKind";
        return {
            accepted: [],
            droppedTotal: files.length,
            rejectReason: reason,
            rejectMaxSizeMb: reason === "tooLarge" ? firstTooLargeMaxSizeMb : undefined,
        };
    }

    const accepted = sliceByRemainingSlots(byPolicy, limits, currentCounts);
    if (accepted.length === 0) {
        return { accepted: [], droppedTotal: files.length, rejectReason: "maxReached" };
    }

    return {
        accepted,
        droppedTotal: files.length - accepted.length,
        rejectReason: null,
    };
}
